import { useCallback, useState, ChangeEvent, KeyboardEvent} from "react";
import { ACCOUNT_USAGE_DEFAULT_FIELDS, APPLICATION_TYPE_ACCOUNT_USAGES } from "./const";
import { AccountUsageType, FormData, AccountTypeFields } from "./types";

const validateEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Default values for a new account creation to prevent empty fields
const DEFAULT_FORM_DATA: FormData = {
  account_usage: AccountUsageType.Developer,
  environment: ['sandbox'],
  admin_email: '',
  brand: 'Appfire',
};

interface AccountInformationFormProps {
  onChange: (data: FormData) => void;
  formData?: FormData;
}

export const useAccountInformationForm = ({
  onChange,
  formData,
}: AccountInformationFormProps) => {
  // Initialize form with provided formData or defaults if empty
  const [localFormData, setLocalFormData] = useState<FormData>(()=>
    formData?.account_usage ? formData : DEFAULT_FORM_DATA
  );
  const [teamMemberError, setTeamMemberError] = useState<string>('');

  // Updates local form state and propagates changes to parent component
  // Merges new updates with existing form data and triggers template form update
  const updateFormData = useCallback((updates: Partial<FormData>) => {
    setLocalFormData(prev => {
      const newData = {
        ...prev,
        ...updates
      };
      onChange(newData);
      return newData;
    });
  }, [onChange]);

  // Generic field change handler that updates a single form field
  const handleFieldChange = useCallback(<K extends keyof FormData>(field: K, value: FormData[K]) => {
    updateFormData({ [field]: value });
  }, [updateFormData]);



  // Handles account usage changes and resets dependent fields
  // Updates admin email, environment with default values based on account usage type
  // Only triggers if the account usage type actually changed
  const handleAccountUsageChange = useCallback((value: AccountUsageType) => {
    if (value !== localFormData.account_usage) {
      const defaultAccountTypeFields = ACCOUNT_USAGE_DEFAULT_FIELDS[value] as AccountTypeFields;
      
      const updatedData: Partial<FormData> = {
        account_usage: value,
        admin_email: defaultAccountTypeFields.admin_email,
        environment: defaultAccountTypeFields.environment,
        brand: localFormData.brand, // Preserve existing brand
      };

      // Reset application-specific fields if switching to non-application type
      if (!APPLICATION_TYPE_ACCOUNT_USAGES.includes(value)) {
        updatedData.application_name = undefined;
        updatedData.application_category = undefined;
        updatedData.team_members = undefined;
      } else {
        // Add application category for app types
        updatedData.application_category = defaultAccountTypeFields.application_category;
      }

      updateFormData(updatedData);
    }
  }, [localFormData.account_usage, localFormData.brand, updateFormData]);

  // Manages environment selection with validation
  const handleEnvironmentToggle = useCallback((value: string, isChecked: boolean) => {
    const currentEnvironments = [...(localFormData.environment || [])];
    
    if (isChecked) {
      handleFieldChange('environment', [...currentEnvironments, value]);
    } else {
      // Prevents removing the last environment to ensure at least one is selected
      if (currentEnvironments.length > 1) {
        const newEnvironments = currentEnvironments.filter(env => env !== value);
        handleFieldChange('environment', newEnvironments);
      }
    }
  }, [localFormData.environment, handleFieldChange]);

  // Handles team member addition via pressing the Enter key
  const handleTeamMemberAdd = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const input = e.target as HTMLInputElement;
      const email = input.value.trim();
      
      if (!email) return;
      // Validates email format and prevents duplicates
      if (!validateEmail(email)) {
        setTeamMemberError('Please enter a valid email address');
        return;
      }

      if (localFormData.team_members?.includes(email)) {
        setTeamMemberError('This email is already in the list');
        return;
      }

      setTeamMemberError('');
      const newMembers = [...(localFormData.team_members || []), email];
      handleFieldChange('team_members', newMembers);
      input.value = '';
    }
  }, [localFormData.team_members, handleFieldChange]);

  // Removes a team member from the list
  const handleTeamMemberDelete = useCallback((memberToDelete: string) => {
    const newMembers = localFormData.team_members?.filter(member => member !== memberToDelete);
    handleFieldChange('team_members', newMembers);
  }, [localFormData.team_members, handleFieldChange]);

  // Clears validation error when user starts typing in team member input
  const handleTeamMemberInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setTeamMemberError('');
    }
  }, []);

  return {
    localFormData,
    teamMemberError,
    handleAccountUsageChange,
    handleFieldChange,
    handleEnvironmentToggle,
    handleTeamMemberDelete,
    handleTeamMemberAdd,
    handleTeamMemberInputChange,
  };
};